import React, { useState, useEffect, useMemo } from 'react';
import { Stack, TextField, Typography, Box } from '@mui/material';
import { useLSContext } from '../Context/useLSContext';
import { useDataContext } from '../Context/useDataContext';
import { useHoleSettings } from '../Context/useHoleSettingsContext';
import { Add, Circle } from '@mui/icons-material';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';
import { calculateHoleStablefordPoints, isStablefordPlayType } from '@moment-golf/leaderboard';
import { MarkerScoreInfo } from './MarkerScoreInfo';

interface ScoreBlockProps {
    blockScoreCode: string;
}

export const ScoreBlock = ({ blockScoreCode }: ScoreBlockProps) => {
    const { factor, scoreBlockID, setScoreBlockID, appTheme } = useLSContext();
    const {
        scoreData,
        setScoreData,
        registrationData,
        eventData,
        scoreCode,
        currentRound,
        isShambleInPlay,
        shamblePlayableDriveMap,
        shambleMinTeamSize,
        hasUnevenShambleTeams
    } = useDataContext();
    const { holeSettings } = useHoleSettings();

    const [tapCount, setTapCount] = useState(0);
    const [blockLoading, setBlockLoading] = useState(true);
    const [maxHoleScore, setMaxHoleScore] = useState(100);

    // console.log('scoreBlockID:', scoreBlockID);
    // console.log('blockScoreCode:', blockScoreCode);

    // Reset tapCount whenever holeSettings change
    useEffect(() => {
       // console.log('SB - Hole Settings:', holeSettings);
       // console.log('SB - Score Block ID:', scoreBlockID);
       // console.log('SB - Score Data:', scoreData);
        setTapCount(0);
        if (eventData.maxHoleScoreInfo) {
            setMaxHoleScore(eventData.maxHoleScoreInfo.overPar === 'par' ? 
                holeSettings.par * 2 : holeSettings.par + eventData.maxHoleScoreInfo.overPar);
        }
    }, [holeSettings, eventData]);

    // Determine if this is the marker's score block, if not opponent, then all scores are player scores
    const marker = eventData.scoreType?.toLowerCase() === 'opponent' ? blockScoreCode === scoreCode : false;
    const scoreKey = marker ? 'markerScore' : 'playerScore';

    const isStableford = isStablefordPlayType(eventData?.rounds?.[currentRound - 1]?.playType);
    const roundHolePoints: Array<any> =
        eventData?.rounds?.[currentRound - 1]?.holePoints ?? [];
    const roundScoreFormat: string = eventData?.rounds?.[currentRound - 1]?.scoreFormat ?? 'gross';
    const stablefordIsNet = roundScoreFormat.toLowerCase() !== 'gross';
    const isBothScoreFormat = roundScoreFormat.toLowerCase() === 'both';

    // Memoize the filtered player data
    const playerData = useMemo(() => {
        return registrationData
            .filter((reg: any) => reg.scoreCode === blockScoreCode)
            .map((player: any) => ({
                playerName: player.playerName,
                teamName: player.teamName
            }));
    }, [registrationData, blockScoreCode]);

    // Memoize score data to prevent unnecessary recalculations
    const { blockEventScore, blockRoundScore, holeScore } = useMemo(() => {
        if (!scoreData || scoreData.length === 0 || !eventData) {
            return { blockEventScore: null, blockRoundScore: null, holeScore: null };
        }

        let blockEventScore;
        
        if (eventData.scoreType?.toLowerCase() === 'opponent') {
            blockEventScore = scoreData[0];
        } else {
            // Find the score without creating a deep clone
            blockEventScore = scoreData.find((score: any) => score.playerScoreCode === blockScoreCode);
        }

        if (!blockEventScore) {
            return { blockEventScore: null, blockRoundScore: null, holeScore: null };
        }

        const blockRoundScore = blockEventScore.roundScores?.find(
            (round: any) => round.roundNumber === currentRound
        );

        if (!blockRoundScore) {
            return { blockEventScore, blockRoundScore: null, holeScore: null };
        }

        const holeScore = blockRoundScore.holeScores?.find(
            (hole: any) => hole.holeNumber === holeSettings.hole
        );

        // console.log('blockEventScore:', blockEventScore);

        return { blockEventScore, blockRoundScore, holeScore };
    }, [scoreData, blockScoreCode, currentRound, holeSettings.hole, eventData]);

    // Max allowed score on this hole = base rule + handicap strokes for this player/marker on this hole
    const effectiveMaxHoleScore = useMemo(() => {
        const base = maxHoleScore;
        const hcpStrokes = holeScore
            ? (marker ? (holeScore.markerHcpAdj ?? 0) : (holeScore.playerHcpAdj ?? 0))
            : 0;
        return base + hcpStrokes;
    }, [maxHoleScore, holeScore, marker]);

    const currentHoleStablefordPoints = useMemo(() => {
        if (!isStableford || !holeScore) return null;
        const raw = marker ? holeScore.markerScore : holeScore.playerScore;
        if (!raw || raw === 0) return 0;
        const hcpAdj = marker ? (holeScore.markerHcpAdj ?? 0) : (holeScore.playerHcpAdj ?? 0);

        const grossPoints = calculateHoleStablefordPoints(
            raw,
            holeScore.par,
            hcpAdj,
            false,
            roundHolePoints
        );
        const netPoints = calculateHoleStablefordPoints(
            raw,
            holeScore.par,
            hcpAdj,
            true,
            roundHolePoints
        );

        if (isBothScoreFormat && hcpAdj !== 0) {
            return `${grossPoints} / ${netPoints}`;
        }

        return roundScoreFormat.toLowerCase() === 'net' ? `${netPoints}` : `${grossPoints}`;
    }, [isStableford, holeScore, marker, roundHolePoints, roundScoreFormat, isBothScoreFormat]);

    const showInPlayBadge = isShambleInPlay(blockScoreCode, holeSettings.hole);

    useEffect(() => {
        console.log('[ScoreBlock] resolved state', {
            blockScoreCode,
            currentRound,
            hole: holeSettings.hole,
            marker,
            scoreKey,
            teamName: playerData?.[0]?.teamName || null,
            playerNames: playerData.map((player: any) => player.playerName),
            currentScore: holeScore?.[scoreKey] ?? null,
            showInPlayBadge,
            shambleMinTeamSize,
            hasUnevenShambleTeams,
            shamblePlayableDriveMap,
            stablefordPoints: currentHoleStablefordPoints,
        });
    }, [
        blockScoreCode,
        currentRound,
        holeSettings.hole,
        marker,
        scoreKey,
        playerData,
        holeScore,
        showInPlayBadge,
        shambleMinTeamSize,
        hasUnevenShambleTeams,
        shamblePlayableDriveMap,
        currentHoleStablefordPoints
    ]);

    // Update loading state when data is available
    useEffect(() => {
        if (holeScore) {
            setBlockLoading(false);
        }
    }, [holeScore]);

    const handleScore_Toggle = (direction: number) => {
        setTapCount(prev => prev + 1);
        
        if (!holeScore) return;
        
        // Get current score value
        const currentScore = holeScore[scoreKey] /* === 0 ? holeSettings.par : holeScore[scoreKey] || holeSettings.par */;
        
        if (currentScore === effectiveMaxHoleScore && direction === 1) return;

        if (currentScore > effectiveMaxHoleScore) {
            console.log('[ScoreBlock] toggle clamped to max', {
                blockScoreCode,
                hole: holeSettings.hole,
                direction,
                currentScore,
                effectiveMaxHoleScore,
            });
            handleScore_Submit(effectiveMaxHoleScore);
        } else {
            // Calculate new score based on direction (+1 or -1), cap at effective max (base + hcp strokes)
            const newScore = Math.min(currentScore + direction, effectiveMaxHoleScore);
            
            // Ensure score doesn't go below 1
            if (newScore >= 1) {
                console.log('[ScoreBlock] toggle submit', {
                    blockScoreCode,
                    hole: holeSettings.hole,
                    direction,
                    currentScore,
                    newScore,
                });
                handleScore_Submit(newScore);
            }
        }
    }

    const handleScore_Submit = (newScore: number) => {
        if (!holeScore) return;
        // Clamp to allowed range: 1 to effective max (base rule + handicap strokes on this hole)
        const clamped = Math.max(1, Math.min(newScore, effectiveMaxHoleScore));
        console.log('[ScoreBlock] submit', {
            blockScoreCode,
            round: currentRound,
            hole: holeSettings.hole,
            scoreKey,
            previousScore: holeScore?.[scoreKey],
            newScore,
            clamped,
        });
        
        // Use immutable update pattern for nested state
        setScoreData((prevScoreData: any) => {
            // Create a deep copy using a more efficient method than JSON.parse/stringify
            return prevScoreData.map((eventScore: any) => {
                // If this isn't the event score we're looking for, return it unchanged
                if (eventData.scoreType?.toLowerCase() === 'opponent') {
                    if (eventScore !== prevScoreData[0]) return eventScore;
                } else {
                    if (eventScore.playerScoreCode !== blockScoreCode) return eventScore;
                }
                
                // Clone the event score
                return {
                    ...eventScore,
                    roundScores: eventScore.roundScores.map((roundScore: any) => {
                        // If this isn't the round we're looking for, return it unchanged
                        if (roundScore.roundNumber !== currentRound) return roundScore;
                        
                        // Clone the round score
                        return {
                            ...roundScore,
                            holeScores: roundScore.holeScores.map((hole: any) => {
                                // If this isn't the hole we're looking for, return it unchanged
                                if (hole.holeNumber !== holeSettings.hole) return hole;
                                
                                // Clone the hole score with the updated score (clamped to effective max)
                                return {
                                    ...hole,
                                    [scoreKey]: clamped
                                };
                            })
                        };
                    })
                };
            });
        });
    }
    
    if (blockLoading || !holeScore || !blockRoundScore) {
        return null; // Or a loading indicator
    }

    const canEdit = blockRoundScore.markerScoreCode === scoreCode;

    const scoreFormat = eventData.rounds[currentRound - 1].scoreFormat.toLowerCase();
    const hcpAdj = marker
        ? (holeScore?.markerHcpAdj ?? 0)
        : (holeScore?.playerHcpAdj ?? 0);
    const showHcpStrokes = scoreFormat !== 'gross' && hcpAdj !== 0;
    const hcpStrokeIcons = showHcpStrokes
        ? (hcpAdj > 0
            ? Array.from({ length: hcpAdj }, (_, index) => (
                <Circle key={index} sx={{ fontSize: 10, color: 'primary.main' }} />
              ))
            : Array.from({ length: Math.abs(hcpAdj) }, (_, index) => (
                <Add key={index} sx={{ fontSize: 12, color: 'primary.main', fontWeight: 700 }} />
              )))
        : null;

    return (
        <Stack id={scoreCode} direction={'row'} sx={{
            alignItems: 'center',
            gap: 1,
            width: '92%',
            justifyContent: 'space-between',
            overflow: 'visible',
            border: '1px solid lightgrey',
            borderRadius: 3 * factor,
            padding: 0.5 * factor,
            minHeight: 40 * factor,
        }}>
            <Stack direction={'row'} sx={{ flex: 1, minWidth: 0, justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Stack direction={'column'} sx={{
                    flex: 1,
                    minWidth: 0,
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    padding: '0px 6px 0px 16px',
                    gap: '0px',
                }}>
                    <Typography variant={playerData[0]?.teamName ? 'h6' : 'h5'} sx={{
                        width: '100%',
                        textAlign: 'left',
                        color: 'primary.dark',
                        pr: 1,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                    }}>
                        {playerData.map((player: any) => player.playerName).join(' / ')}
                    </Typography>
                    {playerData[0]?.teamName &&
                    <Typography variant={'caption'} fontStyle={'italic'} sx={{
                        width: '100%',
                        textAlign: 'left',
                        color: 'primary.dark',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                    }}>
                        {playerData[0]?.teamName || 'Test Team'}
                    </Typography>
                }
                {isStableford && currentHoleStablefordPoints !== null && (
                    <Typography variant={'caption'} sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {currentHoleStablefordPoints}
                    </Typography>
                )}
                </Stack>
                {showInPlayBadge && (
                    <Stack direction="column" sx={{ alignItems: 'center', flexShrink: 0, minWidth: 40, mt: 0.2, mr: 0.5 }}>
                        <SportsGolfIcon sx={{ height: 32, color: 'sepia.main' }} />
                        <Typography variant="subtitle2" sx={{ color: 'sepia.main', fontWeight: 500, lineHeight: 1 }}>
                            In Play
                        </Typography>
                    </Stack>
                )}
            </Stack>
            <Stack direction={'row'} sx={{ 
                alignItems: 'center', 
                gap: '10px', 
                flexShrink: 0,
                overflow: 'visible',
                justifyContent: 'flex-end',
                pr: 0.5,
            }}>
                {marker && <MarkerScoreInfo />}
                <Box
                    sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        overflow: 'visible',
                    }}
                >
                    {hcpStrokeIcons && (
                        <Stack
                            direction="row"
                            sx={{
                                position: 'absolute',
                                // Perched on the top-right of the input so they read as on/inside the box
                                top: 2,
                                right: 3,
                                gap: '2px',
                                zIndex: 10,
                                pointerEvents: 'none',
                                lineHeight: 0,
                            }}
                        >
                            {hcpStrokeIcons}
                        </Stack>
                    )}
                    <Box
                        onPointerDown={(e) => {
                            if (!canEdit) return;
                            e.preventDefault();
                            setScoreBlockID(blockScoreCode);
                        }}
                        sx={{ display: 'inline-flex', cursor: canEdit ? 'pointer' : 'default' }}
                    >
                        <TextField
                            id='score-box'
                            disabled={!canEdit}
                            type="text"
                            value={holeScore[scoreKey] || 0}
                            InputProps={canEdit ? { readOnly: true } : {}}
                            sx={{ 
                                width: 40,
                                height: 40,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                '& .MuiInputBase-root': {
                                    height: '100%',
                                    padding: 0,
                                },
                                '& .MuiInputBase-input': {
                                    textAlign: 'center',
                                    padding: 0,
                                    fontSize: 20,
                                    fontWeight: 500,
                                    height: '100%',
                                    boxSizing: 'border-box',
                                    color: 'primary.dark',
                                    border: scoreBlockID === blockScoreCode ? `2px solid ${appTheme.palette.primary.light}` : 'none',
                                    borderRadius: '4px',
                                },
                                '& .MuiInputBase-root.Mui-disabled .MuiInputBase-input': {
                                    WebkitTextFillColor: (theme) => theme.palette.primary.dark,
                                    color: 'primary.dark',
                                },
                            }}
                            inputProps={{
                                inputMode: 'none',
                            }}
                        />
                    </Box>
                </Box>
            </Stack>
        </Stack>
    );
};